/**
 * The app's destinations, and which one you are looking at.
 *
 * One list, drawn as one group. Settings is a place you go, the same as the other two, so
 * it belongs with them rather than filed away next to the theme and sign-out controls —
 * those are actions, and the rail's foot is where the actions live.
 */

import type { Component } from 'svelte';
import FileText from '@lucide/svelte/icons/file-text';
import FolderKanban from '@lucide/svelte/icons/folder-kanban';
import SettingsIcon from '@lucide/svelte/icons/settings';

export interface Destination {
	href: string;
	label: string;
	icon: Component;
	/**
	 * Other path prefixes this destination is the home of, for routes that do not live
	 * underneath its own href.
	 */
	owns?: string[];
}

export const DESTINATIONS: Destination[] = [
	// The list is at `/` but a report opens at `/reports/<id>`, which is not underneath it.
	// Without `owns` the rail goes completely dark on the screen people spend the most time
	// on - and an icon rail has no labels left to fall back on.
	{ href: '/', label: 'Reports', icon: FileText, owns: ['/reports'] },
	{ href: '/projects', label: 'Projects', icon: FolderKanban },
	{ href: '/settings', label: 'Settings', icon: SettingsIcon }
];

/**
 * Whether this destination is the page currently open.
 *
 * `/` is an exact match; `startsWith` would otherwise light it up on every route in the app.
 * Everything else is a prefix match, so `/projects/abc` still marks Projects as current.
 */
export const isCurrent = ({ href, owns = [] }: Destination, pathname: string) =>
	(href === '/' ? pathname === '/' : pathname.startsWith(href)) ||
	owns.some((prefix) => pathname.startsWith(prefix));
